<template>
  <div class="admin-header">
    <div class="header-gradient">
      <div class="decorative-circle circle-1"></div>
      <div class="decorative-circle circle-2"></div>
      <div :class="['header-top', { compact }]">
        <div class="user-info">
          <div class="avatar" @click="showUserInfo = true">
            <van-icon name="manager" size="24" color="#fff" />
          </div>
          <div class="user-meta">
            <div :class="['top-title', { 'page-title': compact }]">{{ compact ? title : displayCurrentRole }}</div>
            <div class="date-text">{{ todayText }}</div>
          </div>
        </div>
        <div :class="['header-actions', { 'has-switch': showRoleSwitch && roleActions.length >= 1 }]">
          <div v-if="showRoleSwitch && roleActions.length >= 1" class="role-switcher" @click="handleRoleSwitch">
            <span class="role-text">{{ displayCurrentRole }}</span>
            <van-icon name="exchange" size="14" />
          </div>
        </div>
      </div>

      <div class="header-body">
        <div v-if="!compact" class="title">{{ title }}</div>
        <div v-if="subtitle" class="subtitle">{{ subtitle }}</div>
      </div>
    </div>

    <van-action-sheet
      v-model="showRoleSelector"
      :actions="roleActions"
      :cancel-text="$t('btn_cancel')"
      close-on-click-action
      :description="$t('ga_admin_header_switch_role_desc')"
      @select="onSelectRole"
    />

    <van-popup v-model="showUserInfo" round position="center" :style="{ width: '80%', padding: '20px', borderRadius: '12px' }">
      <div class="user-info-card">
        <div class="info-header">
          <div class="avatar-large">
            <van-icon name="manager" size="40" color="#fff" />
          </div>
          <div class="info-title">{{ $t('ga_admin_header_user_info_title') }}</div>
        </div>
        <div class="info-content">
          <div class="info-item">
            <span class="label">{{ $t('ga_admin_header_user_name') }}</span>
            <span class="value">{{ userName || '--' }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('ga_admin_header_user_id') }}</span>
            <span class="value">{{ userId || '--' }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('ga_admin_header_current_role') }}</span>
            <span class="value">{{ displayCurrentRole }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('ga_admin_header_language') }}</span>
            <div class="language-switch">
              <span :class="['lang-option', { active: currentLanguage === 'ZH' }]" @click="switchLanguage('ZH')">{{ $t('ga_admin_header_lang_zh') }}</span>
              <span class="divider">|</span>
              <span :class="['lang-option', { active: currentLanguage === 'EN' }]" @click="switchLanguage('EN')">{{ $t('ga_admin_header_lang_en') }}</span>
            </div>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('ga_admin_header_date') }}</span>
            <span class="value">{{ todayText }}</span>
          </div>
        </div>
        <div class="info-footer">
          <van-button type="info" block round color="#00b852" @click="showUserInfo = false">
            {{ $t('ga_admin_header_close') }}
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { ActionSheet, Button, Icon, Popup, Toast } from 'vant';
import { switchRole } from '@/api/user';
import { getLocalStorage, setLocalStorage, resolveEnableAI, isEnableAI, isSalesManagerTerritory, normalizeSalesOverviewContext, clearManagerOverviewPeriodState } from '@/utils/index';
import { setToken } from '@/utils/auth';

export default {
  name: 'GAAdminHeader',
  components: {
    [ActionSheet.name]: ActionSheet,
    [Button.name]: Button,
    [Icon.name]: Icon,
    [Popup.name]: Popup,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    showRoleSwitch: {
      type: Boolean,
      default: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      userId: getLocalStorage('user_id') || '',
      userName: getLocalStorage('user_name') || '',
      currentRole: getLocalStorage('role_name') || 'GA管理员',
      currentLanguage: getLocalStorage('language') || 'EN',
      showRoleSelector: false,
      showUserInfo: false,
      roleActions: [],
    };
  },
  computed: {
    todayText() {
      const locale = this.currentLanguage === 'ZH' ? 'zh-CN' : 'en-US';
      return new Date().toLocaleDateString(locale, {
        month: 'long',
        day: 'numeric',
      });
    },
    displayCurrentRole() {
      return this.translateRoleName(this.currentRole);
    },
  },
  created() {
    this.fetchRoleOptions();
  },
  methods: {
    translateRoleName(roleName) {
      const roleMap = {
        'GA管理员': this.$t('ga_admin_role_admin'),
        '销售': this.$t('ga_admin_role_sales'),
        MR: this.$t('ga_admin_role_sales'),
        '医学经理': this.$t('ga_admin_role_medical_manager'),
        '医学部管理员': this.$t('ga_admin_role_medical_admin'),
      };
      return roleMap[roleName] || roleName;
    },
    async fetchRoleOptions() {
      try {
        const loginRolesStr = getLocalStorage('login_roles');
        let roles = [];

        if (loginRolesStr) {
          roles = JSON.parse(loginRolesStr);
        }

        const mappedRoles = (roles || [])
          .filter(item => item && item.role_name)
          .sort((a, b) => (Number(a.role_id) > Number(b.role_id) ? 1 : -1))
          .map(item => ({
            name: this.translateRoleName(item.role_name),
            rawName: item.role_name,
            role_id: String(item.role_id),
            color: item.role_name === this.currentRole ? '#00b852' : undefined,
          }));
        this.roleActions = mappedRoles.length
          ? mappedRoles
          : [{
            name: this.displayCurrentRole,
            rawName: this.currentRole,
            role_id: getLocalStorage('role_id') || '',
            color: '#00b852',
          }];
      } catch (error) {
        console.error('Failed to load role actions', error);
        this.roleActions = [{
          name: this.displayCurrentRole,
          rawName: this.currentRole,
          role_id: getLocalStorage('role_id') || '',
          color: '#00b852',
        }];
      }
    },
    handleRoleSwitch() {
      this.showRoleSelector = true;
    },
    switchLanguage(lang) {
      if (this.currentLanguage === lang) return;
      this.currentLanguage = lang;
      if (this.$i18n) {
        this.$i18n.locale = lang;
      }
      setLocalStorage('language', lang);
      this.roleActions = this.roleActions.map((item) => ({
        ...item,
        name: this.translateRoleName(item.rawName || item.name),
      }));
      Toast.success(lang === 'ZH' ? this.$t('ga_admin_switched_to_chinese') : this.$t('ga_admin_switched_to_english'));
    },
    routeByRole(roleName, isMsl) {
      if (roleName === 'GA管理员') {
        this.$router.push({ path: '/ga-admin/overview' });
        return;
      }
      if (roleName === 'GBA' || roleName === 'GA') {
        this.$router.push({ path: '/gba/overview' });
        return;
      }
      if (isMsl) {
        this.$router.push({ path: '/kol/home' });
        return;
      }
      this.$router.push({ path: isSalesManagerTerritory() || isEnableAI() ? '/hcp/overview' : '/hcp/list' });
    },
    async onSelectRole(action) {
      const selectedRoleName = action.rawName || action.name;
      if (this.currentRole === selectedRoleName) {
        this.showRoleSelector = false;
        return;
      }

      try {
        const res = await switchRole(action.role_id);
        if (!(res && res.isSuccess && res.data)) {
          Toast(this.$t('ga_admin_switch_role_failed'));
          return;
        }
        const { token, seed, user } = res.data;
        setToken({ token, seed });
        if (!user) {
          Toast(this.$t('ga_admin_role_info_missing'));
          return;
        }
        clearManagerOverviewPeriodState();
        const normalizedUser = normalizeSalesOverviewContext(user);
        setLocalStorage('is_sales', user.role_name === '销售' ? '1' : '0');
        setLocalStorage('user_id', user.user_id);
        setLocalStorage('user_name', user.user_name);
        setLocalStorage('territory_id', normalizedUser.territory_id);
        setLocalStorage('territory_type', normalizedUser.territory_type);
        setLocalStorage('role_id', user.role_id);
        setLocalStorage('role_name', user.role_name);
        setLocalStorage('enable_ai', resolveEnableAI(user.enable_ai, user.role_id, user.role_name) ? '1' : '0');
        const medicalRoles = ['MSL', 'MA', '医学经理', '医学部管理员'];
        const isMsl = medicalRoles.includes(user.role_name);
        setLocalStorage('is_msl', isMsl ? '1' : '0');
        this.userId = user.user_id;
        this.userName = user.user_name;
        this.currentRole = user.role_name;
        this.roleActions.forEach((item) => {
          item.color = (item.rawName || item.name) === user.role_name ? '#00b852' : undefined;
          item.name = this.translateRoleName(item.rawName || item.name);
        });
        this.routeByRole(user.role_name, isMsl);
      } catch (error) {
        console.error('Switch role failed', error);
        Toast(this.$t('ga_admin_switch_role_failed'));
      }
    },
  },
};
</script>

<style scoped lang="scss">
.admin-header {
  position: relative;
}

.header-gradient {
  padding: 24px 16px 22px;
  background: linear-gradient(165deg, #006b4f 10%, #008f72 30%, #00b852 70%);
  color: #fff;
  border-bottom-left-radius: 22px;
  border-bottom-right-radius: 22px;
  box-shadow: 0 6px 18px rgba(0, 184, 82, 0.18);
  position: relative;
  overflow: hidden;
}

.decorative-circle {
  position: absolute;
  border-radius: 50%;
  filter: blur(30px);
  z-index: 0;
}

.circle-1 {
  width: 220px;
  height: 220px;
  background: rgba(255, 255, 255, 0.25);
  top: -80px;
  left: -60px;
}

.circle-2 {
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.2);
  bottom: -50px;
  right: -40px;
}

.header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.header-top.compact {
  align-items: flex-start;
}

.user-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.user-meta {
  min-width: 0;
}

.avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #00b852 0%, #006b4f 100%);
  border: 1px solid rgba(255, 255, 255, 0.36);
  box-shadow: 0 8px 18px rgba(0, 24, 14, 0.22);
  flex-shrink: 0;
}

.top-title {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
}

.top-title.page-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
}

.date-text {
  margin-top: 3px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.78);
}

.header-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.header-actions.has-switch {
  margin-left: 12px;
}

.role-switcher {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  font-size: 12px;
  cursor: pointer;
}

.role-text {
  font-weight: bold;
}

.header-body {
  margin-top: 18px;
  position: relative;
  z-index: 1;
}

.header-top.compact + .header-body {
  margin-top: 14px;
}

.title {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.subtitle {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.82);
}

.user-info-card {
  text-align: center;

  .info-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 20px;

    .avatar-large {
      width: 80px;
      height: 80px;
      background-color: #00b852;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 12px;
      box-shadow: 0 4px 10px rgba(0,0,0,0.1);
    }

    .info-title {
      font-size: 18px;
      font-weight: bold;
      color: #333;
    }
  }

  .info-content {
    background: #f9f9f9;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;

    .info-item {
      display: flex;
      justify-content: space-between;
      padding: 10px 0;
      border-bottom: 1px dashed #eee;

      &:last-child {
        border-bottom: none;
      }

      .label {
        color: #666;
        font-size: 14px;
      }

      .value {
        color: #333;
        font-weight: 500;
        font-size: 14px;
      }

      .language-switch {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .lang-option {
        color: #999;
        font-size: 14px;
        cursor: pointer;
      }

      .lang-option.active {
        color: #00b852;
        font-weight: 600;
      }

      .divider {
        color: #ccc;
      }
    }
  }
}
</style>
